import { useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useFieldArray, useFormContext } from "react-hook-form";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { formatGroupName } from "components/shared/stage/utils";
import Button from "components/ui/button/Button";
import {
  NewStage,
  NewTransition,
  STAGE_COLORS,
  StageColor,
  StageGroup,
  WorkflowFormValues,
  WorkflowTransition,
} from "./utils";
import { StageInputRow, WorkflowStageRow } from "./WorkflowStageRow";

function getDefaultStageColor(transitions: WorkflowTransition[]): StageColor {
  const matchingIndex = transitions.findIndex((t) =>
    STAGE_COLORS.some((c) => c.stageIconColor === t.stage.iconColor),
  );

  if (matchingIndex !== -1) {
    const matchingStage = transitions[matchingIndex].stage;
    return {
      stageIconColor: matchingStage.iconColor,
      stageBackgroundColor: matchingStage.color,
    };
  }
  return STAGE_COLORS[0];
}

type WorkflowEditorProps = {
  group: StageGroup;
  workflowType: "bid" | "lead";
  generateSearchUrl: (stageId: string) => string;
  onDeleteStage: (group: StageGroup, stageId: string) => void;
  onCreateStage: (
    group: StageGroup,
    newStageName: string,
    iconColor: string,
    backgroundColor: string,
  ) => void;
  onEditStage: (
    stageId: string,
    newStageName: string,
    iconColor: string,
    backgroundColor: string,
  ) => void;
  onReorderStages: (activeTransitionId: string, overTransitionId: string) => void;
};

// Each entry in the current group, with its index in the full flat field array so we can
// pass the correct position to useFieldArray's move().
type CurrentGroup = {
  globalIndex: number;
  field: NewTransition & { key: string };
  /** Stable ID for dnd-kit. Uses the server-assigned transition id when available,
   *  falls back to the RHF-internal key for newly created stages. */
  sortableId: string;
};

export default function WorkflowGroupEditor({
  group,
  onDeleteStage,
  onCreateStage,
  onEditStage,
  onReorderStages,
  generateSearchUrl,
  workflowType,
}: WorkflowEditorProps): JSX.Element {
  const [addStage, setAddStage] = useState<NewStage | null>(null);
  const [editingStage, setEditingStage] = useState<NewStage | null>(null);

  const { control } = useFormContext<WorkflowFormValues>();
  // Sharing the same name/control as the parent WorkflowContainer gives us reactive
  // access to the same field array. No local state or useEffect sync needed.
  const { fields, move } = useFieldArray({ control, name: "transitions", keyName: "key" });

  // Filter to just this current group and capture the global index for move().
  const currentGroup: CurrentGroup[] = useMemo(
    () =>
      fields
        .map((field, globalIndex) => ({ field, globalIndex }))
        .filter(({ field }) => field.group === group.name)
        .map(({ field, globalIndex }) => ({
          globalIndex,
          field,
          sortableId: field.id ?? field.key,
        })),
    [fields, group.name],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const clearInputState = () => {
    setAddStage(null);
    setEditingStage(null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const activeEntry = currentGroup.find((e) => e.sortableId === active.id);
    const overEntry = currentGroup.find((e) => e.sortableId === over.id);
    if (!activeEntry || !overEntry) return;
    // flushSync forces a synchronous render so DndKit sees the new order immediately
    // and doesn't snap the dragged item back to its original position.
    flushSync(() => move(activeEntry.globalIndex, overEntry.globalIndex));
    onReorderStages(active.id as string, over.id as string);
  };

  const handleEditSubmit = () => {
    if (!editingStage?.id || !editingStage.name.trim()) return;
    const { id, name, iconColor, backgroundColor } = editingStage;
    // onEditStage calls replace() in the parent synchronously — fields updates
    // reactively, giving immediate visual feedback without a separate update() call.
    onEditStage(id, name, iconColor, backgroundColor);
    clearInputState();
  };

  const handleAddSubmit = () => {
    if (!addStage?.name.trim()) return;

    const { name, iconColor, backgroundColor } = addStage;
    onCreateStage(group, name, iconColor, backgroundColor);
    clearInputState();
  };

  // Default colour for the add-stage form: match the group's existing stages if possible.
  const { stageIconColor, stageBackgroundColor } = getDefaultStageColor(group.transitions);

  return (
    <Flex
      column
      gap={16}
      key={group.name}
      aria-label={`${formatGroupName(group.name)} stage group`}
    >
      <GroupHeader>
        <Text fontWeight="500">{formatGroupName(group.name)}</Text>
        <Button
          icon="plus"
          iconProps={{ color: "sysTextDefault" }}
          buttonType="tertiary"
          buttonStyle="default"
          aria-label={`Add stage to ${group.name}`}
          onClick={() => {
            setEditingStage(null);
            setAddStage({
              name: "",
              iconColor: stageIconColor,
              backgroundColor: stageBackgroundColor,
            });
          }}
        />
      </GroupHeader>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={currentGroup.map((e) => e.sortableId)}
          strategy={verticalListSortingStrategy}
        >
          <Flex column gap={16}>
            {currentGroup.map((entry) => {
              const { field, sortableId } = entry;
              // Build a WorkflowTransition for the display components.
              // Counts aren't stored in the form, so we look them up from server data.
              const transition: WorkflowTransition = {
                id: sortableId,
                group: field.group,
                order: field.order,
                stage: {
                  id: field.stageId ?? "",
                  name: field.stageName,
                  color: field.stageColor,
                  iconColor: field.stageIconColor,
                  iconName: field.stageIconName,
                  count:
                    group.transitions.find((t) => t.stage.id === field.stageId)?.stage.count ?? 0,
                },
              };

              return editingStage?.id === transition.stage.id ? (
                <StageInputRow
                  key={field.key}
                  group={group}
                  stage={editingStage}
                  setStage={setEditingStage}
                  submitLabel="Save"
                  onSubmit={handleEditSubmit}
                  onCancel={clearInputState}
                />
              ) : (
                <WorkflowStageRow
                  key={field.key}
                  transition={transition}
                  workflowType={workflowType}
                  onStartEdit={() => {
                    setAddStage(null);
                    setEditingStage({
                      id: field.stageId,
                      name: field.stageName,
                      iconColor: field.stageIconColor,
                      backgroundColor: field.stageColor,
                    });
                  }}
                  generateSearchUrl={() => generateSearchUrl(field.stageId ?? "")}
                  onDeleteStage={() => onDeleteStage(group, field.stageId ?? "")}
                />
              );
            })}
            {addStage && (
              <StageInputRow
                group={group}
                stage={addStage}
                setStage={setAddStage}
                submitLabel="Add Stage"
                onSubmit={handleAddSubmit}
                onCancel={clearInputState}
              />
            )}
          </Flex>
        </SortableContext>
      </DndContext>
    </Flex>
  );
}

const GroupHeader = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.sysBackgroundAlternative,
  borderRadius: 8,
  padding: "8px 24px 8px 32px",
  justifyContent: "space-between",
  alignItems: "center",
  alignSelf: "stretch",
}));
